import { GET } from './executer';
import apiServiceInstance from './apiService';
import type { EventSource } from './eventsService';

/**
 * Individual metric aggregate with min/max/avg/sum/count
 * Replaces the hardcoded field pattern (min_air_temp, max_air_temp, etc.)
 * to allow dynamic sensor support.
 */
export interface MetricAggregate {
  name: string;        // e.g., "air_temp", "wind_speed", "pressure"
  min: number | null;
  max: number | null;
  avg: number | null;
  sum: number | null;
  count: number;       // number of readings
}

/**
 * Wind direction is special - not a numeric min/max/avg metric.
 * Represents the most frequent (mode) cardinal direction.
 */
export interface MainWindDirection {
  direction: string;   // cardinal direction (e.g., "nno", "se")
  frequency: number;   // percentage of readings with this direction
  count: number;       // total wind direction readings
}

/**
 * Weather data aggregated per station for a session.
 * Uses flexible metrics array for extensibility.
 */
export interface StationWeatherData {
  station_reference: string;
  metrics: MetricAggregate[];
  main_wind_direction: MainWindDirection | null;
  nb_stations?: number;  // Only populated for "all" aggregate
}

/**
 * Session weather data container.
 */
export interface SessionWeatherData {
  season_year: number;
  circuit_id: string;
  start_date: string; // YYYY-MM-DD format
  session_name: string;
  stations: StationWeatherData[];
}

export interface SessionWeatherResponse {
  success: boolean;
  data: SessionWeatherData[];
  count: number;
  source: string;
  circuit_id?: string;
  session_name?: string;
}

/**
 * Helper function to get a metric by name from a station's metrics array
 */
export const getMetric = (
  station: StationWeatherData,
  metricName: string
): MetricAggregate | undefined => {
  return station.metrics.find(m => m.name === metricName);
};

/**
 * Helper function to get a metric value (avg, min, max, etc.) from a station
 */
export const getMetricValue = (
  station: StationWeatherData,
  metricName: string,
  valueType: 'avg' | 'min' | 'max' | 'sum' | 'count' = 'avg'
): number | null => {
  const metric = getMetric(station, metricName);
  if (!metric) return null;
  return metric[valueType];
};

class SessionWeatherService {
  /**
   * Get session weather data for an event
   */
  async getSessionWeatherByEvent(
    seasonYear: number,
    circuitId: string,
    startDate: string,
    source?: EventSource
  ): Promise<SessionWeatherResponse> {
    const sourceParam = source || 'prod';
    const response = await apiServiceInstance.client.execute<SessionWeatherResponse>(
      GET,
      `/api/session-weather/${seasonYear}/${circuitId}/${startDate}?source=${sourceParam}`
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to fetch session weather data');
    }
    return response.data;
  }

  /**
   * Get historical session weather data for a specific circuit and session name
   */
  async getHistoricalWeatherByCircuitAndSession(
    circuitId: string,
    sessionName: string,
    source?: EventSource
  ): Promise<SessionWeatherResponse> {
    const sourceParam = source || 'prod';
    const response = await apiServiceInstance.client.execute<SessionWeatherResponse>(
      GET,
      `/api/session-weather/circuit/${circuitId}/session/${sessionName}?source=${sourceParam}`
    );
    if (!response.success || !response.data) {
      throw new Error(response.errorData?.message || 'Failed to fetch historical weather data');
    }
    return response.data;
  }
}

export const sessionWeatherService = new SessionWeatherService();
